export const queryKeys = {
  // User / Auth
  me: ['me'] as const,
  networks: ['networks'] as const,
  employees: {
    all: ['employees'] as const,
    list: (params?: object) => ['employees', params] as const,
  },
  shouldShareAllCircuits: ['shouldShareAllCircuits'] as const,

  // Admin
  admin: {
    allReceivers: (params?: object) =>
      ['admin', 'allReceivers', params] as const,
    allDistributors: (params?: object) =>
      ['admin', 'allDistributors', params] as const,
  },

  // Activities
  activities: {
    all: ['activities'] as const,
    cities: (country?: string) => ['activities', 'cities', country] as const,
    detail: (benefitId: string) => ['activities', { benefitId }] as const,
  },

  // Benefits
  benefits: {
    all: ['benefits'] as const,
    list: (params?: object) => ['benefits', params] as const,
    drafts: ['drafts', 'benefits'] as const,
    published: (params?: object) => ['benefits', 'published', params] as const,
    hotelAssociated: (params?: object) =>
      ['benefits', 'hotel-associated-benefits', params] as const,
    cities: (country?: string) => ['benefits', 'cities', country] as const,
    circuits: (benefitId: string) =>
      ['benefits', 'circuits', benefitId] as const,
    circuitsBase: ['benefits', 'circuits'] as const,
  },

  // Countries / Types / Suppliers (benefit-related)
  countriesBenefits: ['countries', 'benefits'] as const,
  typesBenefits: ['types', 'benefits'] as const,
  suppliersBenefits: ['suppliers', 'benefits'] as const,

  // Bills
  bills: ['bills'] as const,

  // Trash
  trash: {
    all: ['trash'] as const,
    count: ['trash', 'count'] as const,
    benefits: (params?: object) => ['trash', 'benefits', params] as const,
    circuits: (params?: object) => ['trash', 'circuits', params] as const,
  },

  // Car rentals
  carRentals: {
    all: ['car-rentals'] as const,
    detail: (benefitId: string) => ['car-rentals', { benefitId }] as const,
  },

  // Circuits
  circuits: {
    all: ['circuits'] as const,
    list: (params?: object) => ['circuits', params] as const,
    distributors: (params?: object) =>
      ['circuits', 'distributors', params] as const,
    distributorsInvalidated: (params?: object) =>
      ['circuits', 'distributors', 'invalidated', params] as const,
    countries: (opts?: { onlyPublished?: boolean }) =>
      ['circuits', 'countries', opts ?? {}] as const,
    durations: ['circuits', 'durations'] as const,
    drafts: ['drafts', 'circuits'] as const,
    detail: (
      id: string,
      opts?: {
        originalImage?: boolean;
        shouldConvertToEuro?: boolean;
        signImages?: 'all' | 'mainOnly';
        skipTranslations?: boolean;
        pricingPreview?: boolean;
      },
    ) => ['circuits', id, opts ?? {}] as const,
    originalImages: (circuitId: string, accessToken?: string) =>
      ['circuits', 'original-images', circuitId, { accessToken }] as const,
    editableCircuits: (params?: object) =>
      ['circuits', 'distributors', 'editable-circuits', params] as const,
    editableCircuitDetail: (editableCircuitId: string) =>
      [
        'circuits',
        'distributors',
        'editable-circuits',
        { editableCircuitId },
      ] as const,
    editableCircuitsBase: [
      'circuits',
      'distributors',
      'editable-circuits',
    ] as const,
    paragraphOverrides: (circuitId: string) =>
      ['circuits', circuitId, 'paragraph-overrides'] as const,
  },

  // Distributors (circuit-related)
  distributorCircuits: {
    receivers: (opts?: { isDistributed?: boolean }) =>
      ['distributors', 'circuits', 'receivers', opts ?? {}] as const,
    countries: (opts?: { isDistributed?: boolean }) =>
      ['distributors', 'circuits', 'countries', opts ?? {}] as const,
    durations: (opts?: { isDistributed?: boolean }) =>
      ['distributors', 'circuits', 'durations', opts ?? {}] as const,
  },

  // Clients
  clients: {
    all: ['clients'] as const,
    list: (params?: object) => ['clients', params] as const,
    detail: (clientId: string) => ['clients', { clientId }] as const,
  },

  // Currency exchange
  currency: {
    latestRates: ['latest-exchange-rates'] as const,
    closestRates: (date?: Date) => ['closest-exchange-rates', date] as const,
    agencyRates: ['agency-exchange-rates'] as const,
    effectiveRates: (date?: Date) =>
      ['effective-exchange-rates', date] as const,
    effectiveRatesBase: ['effective-exchange-rates'] as const,
  },

  // Distributors
  distributors: {
    all: ['distributors'] as const,
    list: (params?: object) => ['distributors', params] as const,
    networks: ['distributors', 'networks'] as const,
    networksList: (params?: object) =>
      ['distributors', 'networks', params] as const,
    countries: ['distributors', 'countries'] as const,
    circuits: ['distributors', 'circuits'] as const,
    circuitsForAgencies: (agencies: string[], networks?: string[]) =>
      [
        'distributors',
        'circuits',
        { agencies, networks: networks ?? [] },
      ] as const,
    agencies: ['distributors', 'agencies'] as const,
    agenciesByCircuit: (circuitIds: string[]) =>
      ['distributors', 'agencies', { circuitIds }] as const,
    agenciesRecent: (params?: object) =>
      ['distributors', 'agencies', 'recent', params] as const,
    agenciesByNetwork: (networkId: string, params?: object) =>
      ['distributors', 'agencies', networkId, params] as const,
    partnershipRequests: (params?: object) =>
      ['distributors', 'partnershipRequests', params] as const,
    partnerAgencyInfo: (agencyId: string) =>
      ['distributors', 'partnerAgencyInfo', agencyId] as const,
  },

  // Drafts (shared)
  drafts: {
    benefits: ['drafts', 'benefits'] as const,
    circuits: ['drafts', 'circuits'] as const,
    suppliers: ['drafts', 'suppliers'] as const,
  },

  // Estimates
  estimates: {
    all: ['estimates'] as const,
    byProject: (projectId: string, options?: object) =>
      ['estimates', projectId, options] as const,
    byProjectBase: (projectId: string) => ['estimates', projectId] as const,
    detail: (projectId: string, estimateId: string) =>
      ['estimates', projectId, { estimateId }] as const,
    info: (projectId: string, estimateId: string) =>
      ['estimates', projectId, 'info', { estimateId }] as const,
    fromSharedLink: (params?: object) =>
      ['estimates', 'shared-link', params] as const,
    fromSharedLinkBase: ['estimates', 'shared-link'] as const,
    agencyWebsite: (estimateId: string) =>
      ['estimates', estimateId, 'agencyWebsite'] as const,
  },

  // Incoming reservation requests
  incomingReservationRequests: {
    all: ['incomingReservationRequests'] as const,
    list: (params?: object) => ['incomingReservationRequests', params] as const,
    detail: (estimateId: string) =>
      ['incomingReservationRequests', { estimateId }] as const,
  },

  // File
  file: {
    byKey: (key: string, original?: boolean, accessToken?: string) =>
      ['file', { key, original, accessToken }] as const,
  },

  // Compressed files
  compressedFiles: (keys: object & { accessToken?: string }) =>
    ['compressedFiles', keys] as const,

  // Flights
  flights: {
    all: ['flights'] as const,
    cities: (country?: string) => ['flights', 'cities', country] as const,
    citiesBase: ['flights', 'cities'] as const,
    airports: (country?: string) => ['flights', 'airports', country] as const,
    airportsBase: ['flights', 'airports'] as const,
    detail: (benefitId: string) => ['flights', { benefitId }] as const,
  },

  // Hotels
  hotels: {
    all: ['hotels'] as const,
    cities: (country?: string) => ['hotels', 'cities', country] as const,
    chains: ['hotels', 'chains'] as const,
    detail: (benefitId: string) => ['hotels', { benefitId }] as const,
  },

  // Projects
  projects: {
    all: ['projects'] as const,
    list: (clientId: string, params?: object) =>
      ['projects', { clientId, ...params }] as const,
    detail: (clientId: string, projectId: string) =>
      ['projects', { clientId, projectId }] as const,
    listBase: (clientId: string) => ['projects', { clientId }] as const,
  },

  // Receivers
  receivers: {
    all: ['receivers'] as const,
    allListBase: ['receivers', 'all'] as const,
    list: (params?: object) => ['receivers', params] as const,
    allList: (params?: object) => ['receivers', 'all', params] as const,
    partnershipRequests: (params?: object) =>
      ['receivers', 'partnershipRequests', params] as const,
    partnerInfo: (receiverId: string) =>
      ['receivers', 'partnerInfo', receiverId] as const,
  },

  // Suppliers
  suppliers: {
    all: ['suppliers'] as const,
    list: (params?: object) => ['suppliers', params] as const,
    detail: (supplierId: string) => ['suppliers', { supplierId }] as const,
    allMySuppliers: ['suppliers', 'allmysuppliers'] as const,
    countries: ['suppliers', 'countries'] as const,
  },

  // Cities (supplier-related, flat key)
  cities: (country?: string) => ['cities', { country }] as const,
  citiesBase: ['cities'] as const,

  // Stats
  stats: {
    all: ['stats'] as const,
    count: (type: string) => ['stats', type] as const,
    distributorsEstimates: (params?: object) =>
      ['stats', 'distributors', 'estimates', params] as const,
    distributorAgents: (params?: object) =>
      ['stats', 'distributor', 'agents', params] as const,
    receiversEstimates: (params?: object) =>
      ['stats', 'receivers', 'estimates', params] as const,
    reservationsReserved: (params?: object) =>
      ['stats', 'reservations', 'reserved', params] as const,
    reservationsRequests: (params?: object) =>
      ['stats', 'reservations', 'requests', params] as const,
    adminReservations: (params?: object) =>
      ['stats', 'admin', 'reservations', params] as const,
    distributorReservations: (params?: object) =>
      ['stats', 'distributor', 'reservations', params] as const,
  },

  // Vehicles with chauffeur
  vehiclesWithChauffeur: {
    all: ['vehicles-with-chauffeur'] as const,
    detail: (benefitId: string) =>
      ['vehicles-with-chauffeur', { benefitId }] as const,
  },
} as const;
